import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, Shield, Clock, Award, TrendingUp, Users, Star, Zap, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProcessSection from '../components/ProcessSection';
import PricingSection from '../components/PricingSection';
import TestimonialsSection from '../components/TestimonialsSection';
import FAQSection from '../components/FAQSection';
import RegistrationModal from '../components/RegistrationModal';
import DialCodeSelect from '../components/DialCodeSelect';
import { getDetectedCountry, submitLead, getCountryList } from '../services/api';

const TrademarkPPCLanding = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: 'US',
    countryDialCode: 1,
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [detectedCountryName, setDetectedCountryName] = useState('');
  const [currentLpBenefit, setCurrentLpBenefit] = useState(0);
  const [geoData, setGeoData] = useState(null);
  const [rawGeo, setRawGeo] = useState(null);
  const countries = getCountryList();

  // Fetch geolocation on mount
  useEffect(() => {
    (async () => {
      const detected = await getDetectedCountry();
      setDetectedCountryName(detected.countryName);
      setGeoData(detected.geoData);
      setRawGeo(detected.rawGeo);
      setFormData(prev => ({
        ...prev,
        country: detected.countryCode,
        countryDialCode: detected.dialCode,
      }));
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Phone number validation: only allow digits and limit to 10 digits
    if (name === 'phone') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/[^0-9]/g, '');
      // Limit to 10 digits
      const limitedDigits = digitsOnly.substring(0, 10);
      setFormData(prev => ({ ...prev, [name]: limitedDigits }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Email validation - comprehensive check
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const trimmedEmail = formData.email.trim();
      
      // Check for @ symbol presence and count
      const atCount = (trimmedEmail.match(/@/g) || []).length;
      
      // Basic structure checks
      if (atCount !== 1) {
        // No @ symbol or multiple @ symbols
        newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      } else {
        const [localPart, domainPart] = trimmedEmail.split('@');
        
        // Check local part (before @)
        if (!localPart || localPart.length === 0) {
          newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
        // Check domain part (after @)
        else if (!domainPart || domainPart.length === 0) {
          newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
        // Check for TLD (domain must have at least one dot)
        else if (!domainPart.includes('.')) {
          newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
        // Check domain has content before and after the dot
        else if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
          newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
        // Check for spaces
        else if (trimmedEmail.includes(' ')) {
          newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
        // Check for consecutive dots
        else if (trimmedEmail.includes('..')) {
          newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
        // Final regex check for valid characters
        else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedEmail)) {
          newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
        }
      }
    }
    
    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setApiError('');

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      phoneNumber: formData.phone,
      countryDialCode: formData.countryDialCode,
      country: formData.country,
      description: formData.message,
      geoData,
      rawGeo,
    });

    setIsSubmitting(false);

    if (result.success) {
      window.location.href = result.redirectUrl;
    } else {
      setApiError(result.error);
    }
  };

  const lpBenefits = [
    { icon: <Shield className="w-8 h-8" />, title: 'Comprehensive Trademark Search', description: 'AI-powered search across millions of trademarks to identify potential conflicts before filing.' },
    { icon: <Clock className="w-8 h-8" />, title: 'Lightning-Fast Filing', description: 'Your application filed with USPTO within 24-48 hours, securing your priority date immediately.' },
    { icon: <Award className="w-8 h-8" />, title: 'Expert Legal Review', description: 'Every application reviewed by licensed USPTO attorneys with years of trademark experience.' },
    { icon: <Users className="w-8 h-8" />, title: 'Dedicated Support Team', description: 'Direct access to your attorney throughout the process. We respond within hours, not days.' },
    { icon: <CheckCircle className="w-8 h-8" />, title: 'Office Action Response', description: 'If USPTO issues an office action, we handle the response at no additional charge.' },
    { icon: <TrendingUp className="w-8 h-8" />, title: 'Lifetime Monitoring', description: 'Track your trademark status and receive alerts for important deadlines and renewals.' },
  ];

  const nextLpBenefit = useCallback(() => setCurrentLpBenefit(p => (p + 1) % lpBenefits.length), [lpBenefits.length]);

  useEffect(() => {
    const timer = setInterval(nextLpBenefit, 4000);
    return () => clearInterval(timer);
  }, [nextLpBenefit]);

  return (
    <>
      <SEO 
        title="Professional Trademark Registration Services | Protect Your Brand Today"
        description="Secure your brand with expert trademark registration. 98% approval rate, licensed USPTO attorneys."
        keywords="trademark registration, brand protection, USPTO filing, trademark attorney, intellectual property"
        canonical="/lp"
      />

      {/* Custom Landing Page Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img src="/logo-dark.png" alt="Norfolk Trademark" className="h-12" />
            </div>

            {/* Right Side Buttons */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsModalOpen(true)}
                className="hidden sm:inline-flex bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-bold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#FF035B]/40"
              >
                Register My Trademark
              </button>
              <button
                onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                className="bg-white border-2 border-[#FF035B] text-[#FF035B] hover:bg-[#FF035B] hover:text-white font-bold px-6 py-3 rounded-lg transition-all duration-300"
              >
                Chat Now
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#FF035B] via-[#2F1A8E] to-black text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Content - Order 2 on mobile, Order 1 on desktop */}
            <div className="order-2 lg:order-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Get Your Brand Officially
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300">
                  Trademarked!
                </span>
              </h1>
              
              <p className="text-sm sm:text-base text-white/90 mb-5 leading-relaxed">
                Build trust with customers immediately! Trademark Verge simplifies the complex process of trademark registration. We protect your brand name and logo so you can grow with confidence. Don't lose customer loyalty because of confusion or copycats—secure your future with us.
              </p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-[#FF035B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  Start your quick, FREE registration check.
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-[#FF035B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  Let our experts handle the legal paperwork
                </li>
                <li className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-[#FF035B] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  Stop copycats and protect your market.
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white text-[#FF035B] hover:bg-[#FF035B] hover:text-white font-bold px-6 py-3 text-sm rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Register My Trademark →
                </button>
                <button
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="border-2 border-white text-white hover:bg-white hover:text-[#2F1A8E] font-semibold px-6 py-3 text-sm rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Speak with an Expert
                </button>
              </div>
            </div>

            {/* Right Form - Order 1 on mobile, Order 2 on desktop */}
            <div className="bg-white rounded-2xl shadow-2xl p-5 order-1 lg:order-2">
              <div className="text-center mb-3">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Protect your brand's trademark at the best price!</h2>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    placeholder="John Doe"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="text"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    data-testid="lp-email-input"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    placeholder="john@example.com"
                  />
                  {errors.email && <p data-testid="lp-email-error" className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number (with country code) *</label>
                  <div className="flex gap-2">
                    <DialCodeSelect
                      value={formData.countryDialCode}
                      onChange={(dialCode) => {
                        const match = countries.find(c => c.dialCode === dialCode);
                        setFormData(prev => ({
                          ...prev,
                          countryDialCode: dialCode,
                          country: match ? match.code : prev.country,
                        }));
                      }}
                      countries={countries}
                      testId="lp-dialcode-select"
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'].includes(e.key) && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text');
                        if (/[^0-9]/.test(pasted)) {
                          e.preventDefault();
                          const digitsOnly = pasted.replace(/[^0-9]/g, '').substring(0, 10);
                          setFormData(prev => ({ ...prev, phone: (prev.phone + digitsOnly).substring(0, 10) }));
                        }
                      }}
                      maxLength="10"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      data-testid="lp-phone-input"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                      placeholder="1234567890"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formData.phone.length}/10 digits</p>
                  {errors.phone && <p data-testid="lp-phone-error" className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tell us about your brand (Optional)</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="2"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF035B] focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)"
                  />
                </div>

                {apiError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {apiError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-bold py-3 text-sm rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Consultation →'}
                </button>

                <p className="text-xs text-center text-gray-500">
                  By submitting, you agree to our Terms & Privacy Policy. No spam, guaranteed.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* What is Trademark Section */}
      <div className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Image - Order 1 on mobile, Order 1 on desktop */}
            <div className="order-1 flex">
              <div className="relative w-full">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop" 
                  alt="Trademark Protection" 
                  className="rounded-2xl shadow-2xl w-full h-full object-cover min-h-[500px] lg:min-h-[600px]"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white p-6 rounded-xl shadow-xl hidden md:block">
                  <div className="text-3xl font-bold mb-1">98%</div>
                  <div className="text-sm">Success Rate</div>
                </div>
              </div>
            </div>

            {/* Content - Order 2 on mobile, Order 2 on desktop */}
            <div className="order-2 flex flex-col justify-between">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why They Matter for Your Business Success
              </h2>
              
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                A trademark is a unique symbol, word, phrase, or design that identifies and distinguishes your products or services from others in the marketplace. It's the legal foundation of your brand identity and a powerful asset that protects your business.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Legal Protection</h3>
                    <p className="text-gray-600 text-sm md:text-base">Prevents competitors from using similar names or logos that could confuse your customers.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Brand Value</h3>
                    <p className="text-gray-600 text-sm md:text-base">Registered trademarks increase business valuation by 20-30% and are essential assets for investors.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Exclusive Rights</h3>
                    <p className="text-gray-600 text-sm md:text-base">Gives you nationwide exclusive rights to use your brand name and logo in your industry.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] hover:from-[#2F1A8E] hover:to-[#FF035B] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#FF035B]/40 inline-flex items-center justify-center gap-2 text-sm"
              >
                Register My Trademark
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Norfolk Trademark?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine legal expertise with modern technology to deliver the fastest, most affordable trademark registration service in the industry.
            </p>
          </div>

          {/* Desktop Grid - Hidden on mobile */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-8 h-8" />,
                title: 'Comprehensive Trademark Search',
                description: 'AI-powered search across millions of trademarks to identify potential conflicts before filing.'
              },
              {
                icon: <Clock className="w-8 h-8" />,
                title: 'Lightning-Fast Filing',
                description: 'Your application filed with USPTO within 24-48 hours, securing your priority date immediately.'
              },
              {
                icon: <Award className="w-8 h-8" />,
                title: 'Expert Legal Review',
                description: 'Every application reviewed by licensed USPTO attorneys with years of trademark experience.'
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: 'Dedicated Support Team',
                description: 'Direct access to your attorney throughout the process. We respond within hours, not days.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Office Action Response',
                description: 'If USPTO issues an office action, we handle the response at no additional charge.'
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: 'Lifetime Monitoring',
                description: 'Track your trademark status and receive alerts for important deadlines and renewals.'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] hover:scale-105">
                <div className="w-16 h-16 bg-[#FF035B] group-hover:bg-white/20 text-white rounded-xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white mb-3 transition-colors duration-500">{benefit.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* Mobile Auto-Slider */}
          <div className="md:hidden">
            <div className="overflow-hidden">
              <div className="transition-transform duration-500 ease-in-out flex" style={{ transform: `translateX(-${currentLpBenefit * 100}%)` }}>
                {lpBenefits.map((benefit, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-2">
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl flex items-center justify-center mb-4">
                        {benefit.icon}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center items-center gap-4 mt-4">
              <button onClick={() => setCurrentLpBenefit(p => (p - 1 + lpBenefits.length) % lpBenefits.length)} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
              <div className="flex gap-2">
                {lpBenefits.map((_, i) => (
                  <button key={i} onClick={() => setCurrentLpBenefit(i)} className={`h-2 rounded-full transition-all ${currentLpBenefit === i ? 'bg-[#FF035B] w-6' : 'bg-gray-300 w-2'}`} />
                ))}
              </div>
              <button onClick={nextLpBenefit} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <ProcessSection />

      {/* Pricing Section */}
      <PricingSection />

      {/* Testimonials */}
      <TestimonialsSection />

      {/* FAQ */}
      <FAQSection />

      {/* Final CTA */}
      <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=600&fit=crop" 
            alt="Background" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/80 via-black/85 to-[#FF035B]/80"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Your Brand Deserves Professional Protection
          </h2>
          <p className="text-xl text-gray-300 mb-4">
            Every day without trademark protection puts your brand at risk. Competitors can file before you, forcing costly rebrands that waste years of marketing investment.
          </p>
          <p className="text-base text-gray-400 mb-8">
            Join 10,000+ smart business owners who secured their brands with our expert trademark services. Get filed with USPTO in 24-48 hours with a 98% approval rate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Get Started - Fill Form Above
            </button>
            <a
              href="tel:+10000000000"
              className="bg-white text-gray-900 hover:bg-gray-100 font-bold px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
            >
              Call Now: +1 0000000000
            </a>
          </div>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span>Free consultation • Licensed USPTO attorneys • No hidden fees</span>
          </div>
        </div>
      </div>

      {/* Simple Landing Page Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo-light.png" alt="Norfolk Trademark" className="h-8" />
              <p className="text-gray-400 text-sm">© 2026 Norfolk Trademark. All Rights Reserved.</p>
            </div>
            <div className="flex gap-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms-conditions" className="text-gray-400 hover:text-white transition-colors">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Registration Modal */}
      <RegistrationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default TrademarkPPCLanding;
